import React, { useState } from "react";
import styles from "./Context.module.css";
import { FiX } from "react-icons/fi";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import { useCreateContext } from "API/Queries/useCreateContext";
import { useParams } from "react-router-dom";
import RequiredStar from "Components/Common/RequiredStar";
import { useGetAllContexts } from "API/Queries/useGetAllContexts";
import Loader from "Components/Loader";
import { Context } from "Store/stateInterfaces";

// Define the data type options for the dropdown
type ContextDataType = "STRING" | "NUMBER";

interface CreateContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (item?: Context) => void;
}

const CreateContextModal: React.FC<CreateContextModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dataType, setDataType] = useState<ContextDataType>("STRING"); // Add state for dataType
  const { mutateAsync: createContext, isLoading: isCreatingContext } =
    useCreateContext();
  const { mutateAsync: getAllContexts, isLoading } = useGetAllContexts();
  const setContexts = useFeatureFlipStore((s) => s.setContexts);
  const { id } = useParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createContext({
        projectId: `${id}`,
        contextName: name,
        contextDescription: description || null,
        dataType, // Add dataType to the payload
      });
      await getAllContexts(`${id}`, {
        onSuccess: (data) => {
          setContexts(data);
          setName("");
          setDescription("");
          setDataType("STRING"); // Reset dataType
          onSuccess(data[0]);
        },
      });
    } catch (error) {
      console.error("Failed to create context:", error);
    }
  };

  if (!isOpen) return null;
  const showLoader = isCreatingContext || isLoading;

  return (
    <div className={styles.modal}>
      {showLoader && <Loader />}
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Create New Context</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label htmlFor="contextName">
              Context Name <RequiredStar />
            </label>
            <input
              id="contextName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter context name"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="contextType">
              Type <RequiredStar />
            </label>
            <select
              id="contextType"
              value={dataType}
              onChange={(e) => setDataType(e.target.value as ContextDataType)}
              required
              style={{ width: "50%", minWidth: "200px" }}
            >
              <option value="STRING">String</option>
              <option value="NUMBER">Number</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="contextDescription">Description</label>
            <textarea
              id="contextDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter context description"
              rows={4}
            />
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={!name.trim()}
            >
              Create Context
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateContextModal;
